import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import {
  Button,
  Field,
  Input,
  Alert,
  Badge,
  Spinner,
  EmptyState,
  SectionTitle,
} from '../../components/ui.jsx';

/**
 * API key management.
 *
 * The one screen where a secret is shown, and it is shown exactly once — keys
 * are stored as a SHA-256 hash, so there is genuinely no way to retrieve one
 * later. The UI has to be honest about that rather than implying it can be
 * looked up again.
 */
export default function ApiKeys() {
  const { isBrand } = useAuth();
  const { data, loading, reload } = useFetch('/v1/keys');

  const [form, setForm] = useState({ name: '', scopes: ['profile:write'] });
  const [minted, setMinted] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // The server returns per-field detail; surface the name error on the name.
  const fieldError = error && /name/i.test(error) ? error : undefined;

  const keys = data?.keys ?? [];
  const availableScopes = data?.availableScopes ?? {};

  const toggleScope = (s) =>
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
    }));

  async function mint(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api.post('/v1/keys', { name: form.name, scopes: form.scopes });
      setMinted(res);
      setForm({ name: '', scopes: ['profile:write'] });
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id) {
    setError('');
    try {
      await api.delete(`/v1/keys/${id}`);
      reload();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <DashboardLayout
      side={isBrand ? 'brand' : 'creator'}
      title="API keys"
      description={
        <>
          For running Affily from code, or letting an agent do it for you. Keys work on the agent
          API (/api/v1) only.{' '}
          <Link to="/for-agents" className="text-accent hover:underline">
            A prompt to give your agent
          </Link>
        </>
      }
    >
      <div className="max-w-3xl space-y-8">
        <Alert>{error}</Alert>

        {minted && (
          <section className="rounded-lg border border-positive/30 bg-positive-soft p-5">
            <h2 className="font-semibold text-positive">Copy this now</h2>
            <p className="mt-1 text-sm text-ink-muted">
              We store a hash, not the key. This is the only time it can be shown — if you lose it,
              revoke it and mint another.
            </p>
            <code className="mt-3 block overflow-x-auto rounded bg-paper-raised px-3 py-2 text-sm">
              {minted.key}
            </code>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(minted.key);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? 'Copied' : 'Copy key'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMinted(null)}>
                I&apos;ve saved it
              </Button>
            </div>
          </section>
        )}

        <section>
          <SectionTitle hint="Grant only what the integration actually needs">
            Create a key
          </SectionTitle>
          <form onSubmit={mint} className="space-y-4 rounded-lg border border-rule bg-paper-raised p-4">
            <Field
              label="What is it for?"
              required
              error={fieldError}
              constraint={rule.max(80)}
              hint="So you know what you're revoking later."
            >
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Production server"
                required
                maxLength={80}
              />
            </Field>

            <fieldset>
              <legend className="mb-2 text-sm font-medium">Scopes</legend>
              <div className="space-y-2">
                {Object.entries(availableScopes).map(([scope, label]) => (
                  <label key={scope} className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={form.scopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                      className="mt-0.5 size-4 accent-accent"
                    />
                    <span>
                      <span className="block text-sm">{label}</span>
                      <code className="text-xs text-ink-faint">{scope}</code>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <p className="text-xs text-ink-faint">
              A key can never change your password, grant itself a role, or mint another key — those
              need you signed in. Firing conversions doesn&apos;t need a key at all; that uses your
              program&apos;s signing secret.
            </p>

            <Button type="submit" disabled={busy || !form.name || !form.scopes.length}>
              {busy ? 'Creating…' : 'Create key'}
            </Button>
          </form>
        </section>

        <section>
          <SectionTitle>Your keys</SectionTitle>
          {loading ? (
            <Spinner />
          ) : keys.length === 0 ? (
            <EmptyState title="No keys yet" />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Key</th>
                    <th>Scopes</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} className={k.isActive ? '' : 'opacity-50'}>
                      <td>
                        <span className="font-medium">{k.name}</span>
                        {k.createdVia === 'signup' && (
                          <Badge className="ml-2">created at signup</Badge>
                        )}
                      </td>
                      <td>
                        <code className="text-xs">{k.prefix}…</code>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {k.scopes.map((s) => (
                            <Badge key={s}>{s}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="text-sm text-ink-muted">
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'never'}
                      </td>
                      <td className="num">
                        {k.isActive ? (
                          <button
                            onClick={() => revoke(k.id)}
                            className="text-xs text-negative hover:underline"
                          >
                            Revoke
                          </button>
                        ) : (
                          <Badge tone="neutral">{k.revokedAt ? 'revoked' : 'expired'}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-rule bg-paper-sunken p-4">
          <h2 className="text-sm font-semibold">Using it</h2>
          <pre className="mt-2 overflow-x-auto rounded bg-ink p-3 text-xs text-paper">
            <code>{`curl ${window.location.origin}/api/v1/me \\
  -H "Authorization: Bearer affily_sk_..."`}</code>
          </pre>
          <p className="mt-2 text-xs text-ink-muted">
            Reading the public directory needs no key at all —{' '}
            <code className="text-ink">/api/programs</code> and{' '}
            <code className="text-ink">/api/creators</code> are open to anyone.
          </p>
        </section>
      </div>
    </DashboardLayout>
  );
}
